import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Lock, Mail, User, ArrowRight, Shield, Key, ArrowLeft, AlertTriangle } from 'lucide-react';

const Login = () => {
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ email: '', password: '', name: '', confirmPassword: '' });
  const [isLoading, setIsLoading] = useState(false);
  const { login, register, completeLogin } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // 2FA states
  const [requires2FA, setRequires2FA] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [backupCode, setBackupCode] = useState('');
  
  // Inactivity logout notification
  const [showInactivityAlert, setShowInactivityAlert] = useState(false);
  
  useEffect(() => {
    if (searchParams.get('reason') === 'inactivity') {
      setShowInactivityAlert(true);
      toast.warning('You were logged out due to inactivity');
    }
  }, [searchParams]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const result = await login(loginData.email, loginData.password);
      
      if (result.requires_2fa) {
        setRequires2FA(true);
        setTempToken(result.temp_token);
        toast.info('Please enter your 2FA code');
      } else {
        toast.success('Welcome back!');
        navigate('/');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handle2FAVerify = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      if (useBackupCode) {
        await completeLogin(tempToken, null, backupCode);
      } else {
        await completeLogin(tempToken, totpCode, null);
      }
      toast.success('Welcome back!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    // Client-side password validation
    const password = registerData.password;
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (!/[A-Z]/.test(password)) {
      toast.error('Password must contain at least one uppercase letter');
      return;
    }
    if (!/[a-z]/.test(password)) {
      toast.error('Password must contain at least one lowercase letter');
      return;
    }
    if (!/\d/.test(password)) {
      toast.error('Password must contain at least one number');
      return;
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      toast.error('Password must contain at least one special character');
      return;
    }
    if (registerData.password !== registerData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setIsLoading(true);
    try {
      await register(registerData.email, registerData.password, registerData.name);
      toast.success('Account created successfully!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setRequires2FA(false);
    setTempToken('');
    setTotpCode('');
    setBackupCode('');
    setUseBackupCode(false);
  };

  // 2FA Verification Form
  if (requires2FA) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(to bottom, #f8fafc, #eef2f7)' }}>
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-4">
            <img 
              src="/assets/logo.png" 
              alt="Nirala Ennem Enterprises" 
              className="mx-auto w-auto max-w-[150px] h-auto rounded-xl"
              style={{ maxHeight: '150px' }}
            />
          </div>
          
          {/* Subtitle Only - Company name is in the logo */}
          <div className="text-center mb-6">
            <p className="text-slate-500 text-sm" style={{ marginTop: '8px' }}>
              GST Invoice & Inventory Management
            </p>
          </div>

          <Card 
            className="border-0 bg-white/95 backdrop-blur"
            style={{ 
              boxShadow: '0 10px 25px rgba(0,0,0,0.08)',
              borderRadius: '12px'
            }}
          >
            <CardHeader className="space-y-1 pb-4">
              <div className="flex items-center justify-center gap-2 text-teal-600 mb-2">
                <Shield className="w-8 h-8" />
              </div>
              <CardTitle className="text-xl text-center tracking-tight">Two-Factor Authentication</CardTitle>
              <CardDescription className="text-center">
                {useBackupCode 
                  ? 'Enter one of your backup recovery codes'
                  : 'Enter the 6-digit code from your authenticator app'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handle2FAVerify} className="space-y-4">
                {useBackupCode ? (
                  <div className="space-y-2">
                    <Label htmlFor="backup-code" className="text-sm font-medium text-slate-700">Backup Code</Label>
                    <div className="relative">
                      <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="backup-code"
                        type="text"
                        placeholder="XXXXXXXX"
                        value={backupCode}
                        onChange={(e) => setBackupCode(e.target.value.toUpperCase())}
                        className="pl-10 h-11 bg-slate-50 border-slate-200 focus:bg-white text-center font-mono tracking-widest"
                        data-testid="backup-code-input"
                        autoFocus
                        required
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label htmlFor="totp-code" className="text-sm font-medium text-slate-700">Verification Code</Label>
                    <div className="relative">
                      <Shield className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="totp-code"
                        type="text"
                        maxLength={6}
                        placeholder="000000"
                        value={totpCode}
                        onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                        className="pl-10 h-11 bg-slate-50 border-slate-200 focus:bg-white text-center text-2xl font-mono tracking-widest"
                        data-testid="totp-code-input"
                        autoFocus
                        required
                      />
                    </div>
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full h-11 bg-teal-600 hover:bg-teal-700 text-white font-medium shadow-sm transition-all duration-200 ease-in-out"
                  disabled={isLoading || (useBackupCode ? !backupCode : totpCode.length !== 6)}
                  data-testid="verify-2fa-btn"
                >
                  {isLoading ? 'Verifying...' : (
                    <>
                      Verify & Sign In
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-slate-200" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-white px-2 text-slate-500">or</span>
                  </div>
                </div>

                <Button
                  type="button"
                  variant="outline"
                  className="w-full transition-all duration-200 ease-in-out"
                  onClick={() => {
                    setUseBackupCode(!useBackupCode);
                    setTotpCode('');
                    setBackupCode('');
                  }}
                  data-testid="toggle-backup-code-btn"
                >
                  <Key className="w-4 h-4 mr-2" />
                  {useBackupCode ? 'Use Authenticator App' : 'Use Backup Code'}
                </Button>

                <Button
                  type="button"
                  variant="ghost"
                  className="w-full text-slate-500 transition-all duration-200 ease-in-out"
                  onClick={handleBackToLogin}
                  data-testid="back-to-login-btn"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Login
                </Button>
              </form>
            </CardContent>
          </Card>

          <p className="text-center text-xs text-slate-500 mt-6">
            © 2026 Nirala Ennem Enterprises. All rights reserved.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(to bottom, #f8fafc, #eef2f7)' }}>
      <div className="w-full max-w-md">
        {/* Inactivity Alert */}
        {showInactivityAlert && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2 text-amber-800">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm">You were logged out due to 15 minutes of inactivity. Please sign in again.</span>
          </div>
        )}
        
        {/* Logo */}
        <div className="text-center mb-4">
          <img 
            src="/assets/logo.png" 
            alt="Nirala Ennem Enterprises" 
            className="mx-auto w-auto max-w-[120px] sm:max-w-[150px] h-auto rounded-xl"
            style={{ maxHeight: '150px' }}
          />
        </div>
        
        {/* Subtitle Only - Company name is in the logo */}
        <div className="text-center mb-6">
          <p className="text-slate-500 text-sm" style={{ marginTop: '8px' }}>
            GST Invoice & Inventory Management
          </p>
        </div>

        <Card 
          className="border-0 bg-white/95 backdrop-blur"
          style={{ 
            boxShadow: '0 10px 25px rgba(0,0,0,0.08)',
            borderRadius: '12px'
          }}
        >
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-xl text-center tracking-tight">Welcome</CardTitle>
            <CardDescription className="text-center">
              Sign in to your account or create a new one
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="login" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6 p-1 bg-slate-100">
                <TabsTrigger value="login" data-testid="login-tab" className="data-[state=active]:bg-white data-[state=active]:shadow-sm transition-all duration-200">
                  Sign In
                </TabsTrigger>
                <TabsTrigger value="register" data-testid="register-tab" className="data-[state=active]:bg-white data-[state=active]:shadow-sm transition-all duration-200">
                  Register
                </TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email" className="text-sm font-medium text-slate-700">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="admin@example.com"
                        value={loginData.email}
                        onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                        className="pl-10 h-11 bg-slate-50 border-slate-200 focus:bg-white transition-all duration-200"
                        data-testid="login-email-input"
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password" className="text-sm font-medium text-slate-700">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="login-password"
                        type="password"
                        placeholder="••••••••"
                        value={loginData.password}
                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                        className="pl-10 h-11 bg-slate-50 border-slate-200 focus:bg-white transition-all duration-200"
                        data-testid="login-password-input"
                        required
                      />
                    </div>
                  </div>
                  <Button
                    type="submit"
                    className="w-full h-11 bg-teal-600 hover:bg-teal-700 text-white font-medium shadow-sm transition-all duration-200 ease-in-out"
                    disabled={isLoading}
                    data-testid="login-submit-btn"
                  >
                    {isLoading ? 'Signing in...' : (
                      <>
                        Sign In
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="register-name" className="text-sm font-medium text-slate-700">Full Name</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="register-name"
                        type="text"
                        placeholder="John Doe"
                        value={registerData.name}
                        onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                        className="pl-10 h-11 bg-slate-50 border-slate-200 focus:bg-white transition-all duration-200"
                        data-testid="register-name-input"
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-email" className="text-sm font-medium text-slate-700">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="register-email"
                        type="email"
                        placeholder="admin@example.com"
                        value={registerData.email}
                        onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                        className="pl-10 h-11 bg-slate-50 border-slate-200 focus:bg-white transition-all duration-200"
                        data-testid="register-email-input"
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-password" className="text-sm font-medium text-slate-700">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="register-password"
                        type="password"
                        placeholder="••••••••"
                        value={registerData.password}
                        onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                        className="pl-10 h-11 bg-slate-50 border-slate-200 focus:bg-white transition-all duration-200"
                        data-testid="register-password-input"
                        required
                        minLength={8}
                      />
                    </div>
                    <p className="text-xs text-slate-500">Min 8 chars, uppercase, lowercase, number, special char</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-confirm-password" className="text-sm font-medium text-slate-700">Confirm Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="register-confirm-password"
                        type="password"
                        placeholder="••••••••"
                        value={registerData.confirmPassword}
                        onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
                        className="pl-10 h-11 bg-slate-50 border-slate-200 focus:bg-white transition-all duration-200"
                        data-testid="register-confirm-password-input"
                        required
                      />
                    </div>
                  </div>
                  <Button
                    type="submit"
                    className="w-full h-11 bg-teal-600 hover:bg-teal-700 text-white font-medium shadow-sm transition-all duration-200 ease-in-out"
                    disabled={isLoading}
                    data-testid="register-submit-btn"
                  >
                    {isLoading ? 'Creating account...' : (
                      <>
                        Create Account
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-slate-500 mt-6">
          © 2026 Nirala Ennem Enterprises. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default Login;
